import { trackAddToCart } from '../utils/metaPixel'

// Maps each internal product id (from data/kits.ts and data/upsells.ts) to its
// real Shopify Variant ID. Used to build a direct /cart permalink so Shopify
// opens with exactly the selected products ready to checkout.
const VARIANT_IDS: Record<string, string> = {
  // Main kits
  completa: '53306292535596', // Kit Alimentación Completa
  cardio: '53306819346732', // Kit Salud Cardiovascular
  fitness: '53306820067628', // Kit Fitness & Proteínas
  // Complementary kits
  antiinflamatorio: '53306834026796',
  alergias: '53306822066476',
  colon: '53306831864108',
  hipotiroidismo: '53306834616620',
  menopausia: '53306835796268',
  lactosa: '53306824327468', // Kit Sin Lactosa
}

// Promo-specific Shopify Variant IDs for the "2 complementos por $19.990"
// offer (the ExtraComplementUpsellPopup). When the customer accepts that
// promo, the complementary kits must redirect to these variants instead of
// the standalone complement variants above.
const TWO_COMPLEMENTS_PROMO_VARIANT_IDS: Record<string, string> = {
  alergias: '53326866907436',
  antiinflamatorio: '53326869561644',
  colon: '53326868676908',
  hipotiroidismo: '53326873952556',
  menopausia: '53326874738988',
  lactosa: '53326862778668', // Kit Sin Lactosa
}

// Promo-specific Shopify Variant IDs for the "2 productos por $22.990" offer
// (the SecondKitUpsellPopup). When the customer accepts that promo, both the
// main kits and the complementary kits must redirect to these variants.
const TWO_PRODUCTS_PROMO_VARIANT_IDS: Record<string, string> = {
  // Main kits
  completa: '53326911865132', // Kit Alimentación Completa
  cardio: '53326913634604', // Kit Salud Cardiovascular
  fitness: '53326916354348', // Kit Fitness & Proteínas
  // Complementary kits
  alergias: '53326866907436',
  antiinflamatorio: '53326869561644',
  colon: '53326868676908',
  hipotiroidismo: '53326873952556',
  menopausia: '53326874738988',
  lactosa: '53326862778668', // Kit Sin Lactosa
}

// Promo-specific Shopify Variant IDs for the full-bundle "todos los
// complementos al 30% OFF" offer (the BundleUpsellPopup). When the customer
// accepts that promo, the complementary kits must redirect to these variants.
const BUNDLE_PROMO_VARIANT_IDS: Record<string, string> = {
  alergias: '53327979643180',
  antiinflamatorio: '53327985180972',
  colon: '53327983804716',
  hipotiroidismo: '53327972401452',
  menopausia: '53327986852140',
  lactosa: '53327981117740', // Kit Sin Lactosa
}

// Promo-specific Shopify Variant IDs for the "2 productos por $22.990" offer
// WHEN both products are main kits (kit + kit). The main kits use these
// variants instead of the standard or single-product promo ones.
const TWO_KITS_PROMO_VARIANT_IDS: Record<string, string> = {
  completa: '53326964392236', // Kit Alimentación Completa
  cardio: '53326966063404', // Kit Salud Cardiovascular
  fitness: '53326964359468', // Kit Fitness & Proteínas
}

// Variant IDs for the "Pack Completo" offer (all 9 kits together). When the
// customer clicks "QUIERO EL PACK COMPLETO", the cart must open with exactly
// these variants.
const FULL_PACK_PROMO_VARIANT_IDS: string[] = [
  '53406733238572', // Kit Alimentación Completa
  '53406695162156', // Kit Antiinflamatorio
  '53406697521452', // Kit Alergias Alimentarias
  '53406696014124', // Kit Colon Irritable
  '53406734025004', // Kit Fitness & Proteínas
  '53406731141420', // Kit Hipotiroidismo
  '53406661673260', // Kit Post Menopausia
  '53406732976428', // Kit Salud Cardiovascular
  '53406696866092', // Kit Sin Lactosa
]

const SHOPIFY_CART_BASE = 'https://www.recetaspowerfit.com/cart'

/**
 * Fire the Meta Pixel AddToCart event, then redirect to the Shopify cart.
 * `items` is the list of "variantId:1" permalink segments.
 */
function redirectToCart(items: string[]) {
  if (items.length === 0) return
  trackAddToCart({
    contents: items.map((segment) => {
      const [id, qty] = segment.split(':')
      return { id, quantity: Number(qty) || 1 }
    }),
  })
  window.location.href = `${SHOPIFY_CART_BASE}/${items.join(',')}`
}

/**
 * Build a Shopify cart permalink from the selected product ids.
 * Format: https://.../cart/VARIANT_ID:1,VARIANT_ID:1,...
 * Returns null when none of the ids map to a known variant.
 */
export function buildShopifyCartUrl(productIds: string[]): string | null {
  const items = productIds
    .map((id) => VARIANT_IDS[id])
    .filter((variantId): variantId is string => Boolean(variantId))
    .map((variantId) => `${variantId}:1`)

  if (items.length === 0) return null
  return `${SHOPIFY_CART_BASE}/${items.join(',')}`
}

/**
 * Redirect for the "Pack Completo" offer. Opens the Shopify cart with all 9
 * kits using their dedicated pack variant ids.
 */
export function goToShopifyCartFullPack() {
  const items = FULL_PACK_PROMO_VARIANT_IDS.map((variantId) => `${variantId}:1`)
  redirectToCart(items)
}

/**
 * Build the cart URL from the selected ids and redirect the browser to it.
 * If no selected id maps to a real variant, it does nothing (keeps the
 * current page behavior).
 */
export function goToShopifyCart(productIds: string[]) {
  const items = productIds
    .map((id) => VARIANT_IDS[id])
    .filter((variantId): variantId is string => Boolean(variantId))
    .map((variantId) => `${variantId}:1`)
  redirectToCart(items)
}

/**
 * Redirect for the "2 complementos por $19.990" promo. The two complementary
 * kits use their PROMO variant ids; any selected main kits keep their standard
 * variant ids. Does nothing if nothing maps to a known variant.
 */
export function goToShopifyCartTwoComplementsPromo(
  kitIds: string[],
  complementIds: string[],
) {
  const items = [
    ...kitIds
      .map((id) => VARIANT_IDS[id])
      .filter((v): v is string => Boolean(v)),
    ...complementIds
      .map((id) => TWO_COMPLEMENTS_PROMO_VARIANT_IDS[id])
      .filter((v): v is string => Boolean(v)),
  ].map((variantId) => `${variantId}:1`)

  redirectToCart(items)
}

/**
 * Redirect for the "2 productos por $22.990" promo (SecondKitUpsellPopup).
 * Both main kits and complementary kits use their PROMO variant ids. Does
 * nothing if nothing maps to a known variant.
 */
export function goToShopifyCartTwoProductsPromo(productIds: string[]) {
  const items = productIds
    .map((id) => TWO_PRODUCTS_PROMO_VARIANT_IDS[id])
    .filter((v): v is string => Boolean(v))
    .map((variantId) => `${variantId}:1`)

  redirectToCart(items)
}

/**
 * Redirect for the "2 productos por $22.990" promo WHEN both products are main
 * kits (kit + kit). The main kits use their TWO-KITS promo variant ids. Does
 * nothing if nothing maps to a known variant.
 */
export function goToShopifyCartTwoKitsPromo(kitIds: string[]) {
  const items = kitIds
    .map((id) => TWO_KITS_PROMO_VARIANT_IDS[id])
    .filter((v): v is string => Boolean(v))
    .map((variantId) => `${variantId}:1`)

  redirectToCart(items)
}

/**
 * Redirect for the full-bundle "todos los complementos al 30% OFF" promo
 * (BundleUpsellPopup). The complementary kits use their BUNDLE PROMO variant
 * ids; any selected main kits keep their standard variant ids. Does nothing
 * if nothing maps to a known variant.
 */
export function goToShopifyCartBundlePromo(
  kitIds: string[],
  complementIds: string[],
) {
  const items = [
    ...kitIds
      .map((id) => VARIANT_IDS[id])
      .filter((v): v is string => Boolean(v)),
    ...complementIds
      .map((id) => BUNDLE_PROMO_VARIANT_IDS[id])
      .filter((v): v is string => Boolean(v)),
  ].map((variantId) => `${variantId}:1`)

  redirectToCart(items)
}
